import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="checkout"
export default class extends Controller {
    static targets = [
        "email", "submitButton", "warning", "overlapWarning",
        "paymentPlanInput",
        "depositOption", "fullOption",
        "depositSummary", "fullSummary",
        "selectedPlanBadge"
    ]
    static values = {
        sessionId: String,
        coursePrice: Number,
        depositAmount: Number
    }

    connect() {
        this.isAlreadyBooked = false
        this.hasOverlap = false
        // Default to deposit
        this.selectDeposit()
    }

    selectDeposit() {
        if (this.hasPaymentPlanInputTarget) this.paymentPlanInputTarget.value = 'deposit'

        // Toggle card styles
        if (this.hasDepositOptionTarget) {
            this.depositOptionTarget.classList.add('ring-2', 'ring-blue-500', 'border-blue-500', 'bg-blue-50/50')
            this.depositOptionTarget.classList.remove('border-slate-200', 'hover:border-slate-300')
            // Icon contrast
            const icon = this.depositOptionTarget.querySelector('.rounded-xl')
            if (icon) icon.classList.replace('bg-blue-100', 'bg-blue-600')
            if (icon) icon.classList.replace('text-blue-600', 'text-white')
            // Radio indicator
            const radio = this.depositOptionTarget.querySelector('.absolute .rounded-full')
            if (radio) radio.classList.add('bg-blue-500', 'border-blue-500')
            if (radio) radio.innerHTML = '<svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>'
        }
        if (this.hasFullOptionTarget) {
            this.fullOptionTarget.classList.remove('ring-2', 'ring-blue-500', 'border-blue-500', 'bg-blue-50/50')
            this.fullOptionTarget.classList.add('border-slate-200', 'hover:border-slate-300')
            // Icon contrast
            const icon = this.fullOptionTarget.querySelector('.rounded-xl')
            if (icon) icon.classList.replace('bg-emerald-600', 'bg-emerald-100')
            if (icon) icon.classList.replace('text-white', 'text-emerald-600')
            // Radio indicator
            const radio = this.fullOptionTarget.querySelector('.absolute .rounded-full')
            if (radio) radio.classList.remove('bg-emerald-500', 'border-emerald-500')
            if (radio) radio.classList.add('border-slate-300')
            if (radio) radio.innerHTML = ''
        }

        // Toggle summary sections
        if (this.hasDepositSummaryTarget) this.depositSummaryTarget.classList.remove('hidden')
        if (this.hasFullSummaryTarget) this.fullSummaryTarget.classList.add('hidden')

        // Update badge
        if (this.hasSelectedPlanBadgeTarget) {
            this.selectedPlanBadgeTarget.innerText = 'Deposit'
            this.selectedPlanBadgeTarget.classList.replace('bg-emerald-100', 'bg-blue-100')
            this.selectedPlanBadgeTarget.classList.replace('text-emerald-700', 'text-blue-700')
        }

        // Update submit button
        const deposit = this.depositAmountValue || 0
        const formatted = this.formatCurrency(deposit)
        if (this.hasSubmitButtonTarget) this.submitButtonTarget.value = `Secure Booking - ${formatted} Deposit`
    }

    selectFull() {
        if (this.hasPaymentPlanInputTarget) this.paymentPlanInputTarget.value = 'full'

        // Toggle card styles
        if (this.hasFullOptionTarget) {
            this.fullOptionTarget.classList.add('ring-2', 'ring-blue-500', 'border-blue-500', 'bg-blue-50/50')
            this.fullOptionTarget.classList.remove('border-slate-200', 'hover:border-slate-300')
            // Icon contrast
            const icon = this.fullOptionTarget.querySelector('.rounded-xl')
            if (icon) icon.classList.replace('bg-emerald-100', 'bg-emerald-600')
            if (icon) icon.classList.replace('text-emerald-600', 'text-white')
            // Radio indicator
            const radio = this.fullOptionTarget.querySelector('.absolute .rounded-full')
            if (radio) radio.classList.add('bg-emerald-500', 'border-emerald-500')
            if (radio) radio.innerHTML = '<svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>'
        }
        if (this.hasDepositOptionTarget) {
            this.depositOptionTarget.classList.remove('ring-2', 'ring-blue-500', 'border-blue-500', 'bg-blue-50/50')
            this.depositOptionTarget.classList.add('border-slate-200', 'hover:border-slate-300')
            // Icon contrast
            const icon = this.depositOptionTarget.querySelector('.rounded-xl')
            if (icon) icon.classList.replace('bg-blue-600', 'bg-blue-100')
            if (icon) icon.classList.replace('text-white', 'text-blue-600')
            // Radio indicator
            const radio = this.depositOptionTarget.querySelector('.absolute .rounded-full')
            if (radio) radio.classList.remove('bg-blue-500', 'border-blue-500')
            if (radio) radio.classList.add('border-slate-300')
            if (radio) radio.innerHTML = ''
        }

        // Toggle summary sections
        if (this.hasFullSummaryTarget) this.fullSummaryTarget.classList.remove('hidden')
        if (this.hasDepositSummaryTarget) this.depositSummaryTarget.classList.add('hidden')

        // Update badge
        if (this.hasSelectedPlanBadgeTarget) {
            this.selectedPlanBadgeTarget.innerText = 'Paid in Full'
            this.selectedPlanBadgeTarget.classList.replace('bg-blue-100', 'bg-emerald-100')
            this.selectedPlanBadgeTarget.classList.replace('text-blue-700', 'text-emerald-700')
        }

        // Update submit button
        const price = this.coursePriceValue
        const formatted = this.formatCurrency(price)
        if (this.hasSubmitButtonTarget) this.submitButtonTarget.value = `Pay Full - ${formatted}`
    }

    formatCurrency(amount) {
        const rounded = Math.round((amount || 0) * 100) / 100
        return rounded % 1 === 0 ? `£${rounded}` : `£${rounded.toFixed(2)}`
    }

    checkExistingBooking() {
        const email = this.emailTarget.value.trim()

        if (!email) {
            this.hideWarning()
            this.hideOverlapWarning()
            this.isAlreadyBooked = false
            this.hasOverlap = false
            this.updateSubmitButton()
            return
        }

        if (!this.sessionIdValue) return

        fetch(`/checkouts/check_booking/${this.sessionIdValue}?email=${encodeURIComponent(email)}`, {
            headers: { "Accept": "application/json" }
        })
            .then(response => response.json())
            .then(data => {
                if (data.already_booked) {
                    this.showWarning()
                    this.isAlreadyBooked = true
                    this.hideOverlapWarning()
                    this.hasOverlap = false
                } else if (data.overlapping_session) {
                    this.hideWarning()
                    this.isAlreadyBooked = false
                    this.showOverlapWarning(data.overlapping_course)
                    this.hasOverlap = true
                } else {
                    this.hideWarning()
                    this.hideOverlapWarning()
                    this.isAlreadyBooked = false
                    this.hasOverlap = false
                }
                this.updateSubmitButton()
            })
            .catch(() => {
                this.hideWarning()
                this.hideOverlapWarning()
                this.isAlreadyBooked = false
                this.hasOverlap = false
                this.updateSubmitButton()
            })
    }

    showWarning() {
        if (this.hasWarningTarget) {
            this.warningTarget.classList.remove("hidden")
            this.warningTarget.classList.add("flex")
        }
        this.emailTarget.classList.add("border-amber-300", "focus:border-amber-500", "focus:ring-amber-500/10")
        this.emailTarget.classList.remove("border-slate-200", "focus:border-blue-500", "focus:ring-blue-500/10")
    }

    hideWarning() {
        if (this.hasWarningTarget) {
            this.warningTarget.classList.add("hidden")
            this.warningTarget.classList.remove("flex")
        }
        this.emailTarget.classList.remove("border-amber-300", "focus:border-amber-500", "focus:ring-amber-500/10")
        this.emailTarget.classList.add("border-slate-200", "focus:border-blue-500", "focus:ring-blue-500/10")
    }

    showOverlapWarning(courseName) {
        if (this.hasOverlapWarningTarget) {
            const span = this.overlapWarningTarget.querySelector("span")
            if (span) {
                span.textContent = `You already have a booking for "${courseName}" scheduled at the same time. You cannot attend two sessions simultaneously.`
            }
            this.overlapWarningTarget.classList.remove("hidden")
            this.overlapWarningTarget.classList.add("flex")
        }
        this.emailTarget.classList.add("border-amber-300", "focus:border-amber-500", "focus:ring-amber-500/10")
        this.emailTarget.classList.remove("border-slate-200", "focus:border-blue-500", "focus:ring-blue-500/10")
    }

    hideOverlapWarning() {
        if (this.hasOverlapWarningTarget) {
            this.overlapWarningTarget.classList.add("hidden")
            this.overlapWarningTarget.classList.remove("flex")
        }
    }

    updateSubmitButton() {
        if (!this.hasSubmitButtonTarget) return

        const blocked = this.isAlreadyBooked || this.hasOverlap
        this.submitButtonTarget.disabled = blocked
        if (blocked) {
            this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
        } else {
            this.submitButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
        }
    }
}
